/**
 * Main App Component
 * Force redeploy 2026-01-08.003 - Verify ECharts formatter fix in production bundle
 */
import React, { Suspense, lazy, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation } from 'react-router';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { AuthProvider } from './auth/AuthProvider.jsx';
import { NotificationProvider } from './contexts/NotificationContext.jsx';
import { ActivityProvider } from './contexts/ActivityContext.jsx';
import { CSRFProvider } from './hooks/useCSRFToken.jsx';
import NotificationToastContainer from './components/notifications/NotificationToastContainer.jsx';
import { setupApiClient } from './services/apiClient.js';
import { initializeRealtimeConnection, realtimeClient } from './services/realtimeClient.js';
import appInsightsService from './services/appInsightsService.js';
import Logger from './utils/logger.js';
import { initializeWebVitalsTracking, trackPageLoadPerformance } from './utils/webVitalsReporter.js';
import { runAuthDiagnostics, printDiagnostics } from './utils/authValidation.js';
import { installDebugHelpers } from './utils/tokenDebugger.js';
import './App.css';
import LoadingSpinner from './components/common/LoadingSpinner.jsx';
import ErrorBoundary from './components/common/ErrorBoundary.jsx';
import AuthErrorBoundary from './components/common/AuthErrorBoundary.jsx';
import ProtectedRoute from './components/Auth/ProtectedRoute.jsx';
import TVDisplayRedirect from './components/Auth/TVDisplayRedirect.jsx';
import ConnectionStatus from './components/ConnectionStatus.jsx';

// Lazy loading for pages (code splitting for better performance)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Buses = lazy(() => import('./pages/Buses'));
const Schedule = lazy(() => import('./pages/Schedule'));
const SchedulerDashboard = lazy(() => import('./pages/SchedulerDashboard'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const TvDisplay = lazy(() => import('./pages/TvDisplay'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ConflictHistory = lazy(() => import('./pages/ConflictHistory'));
const Analytics = lazy(() => import('./pages/Analytics'));
const SMSTemplates = lazy(() => import('./pages/admin/SMSTemplates'));
const SMSBroadcast = lazy(() => import('./pages/admin/SMSBroadcast'));
const SMSLogs = lazy(() => import('./pages/admin/SMSLogs'));
const EventTriggers = lazy(() => import('./pages/admin/EventTriggers'));
const TriggerForm = lazy(() => import('./components/eventTriggers/TriggerForm'));
const FleetMap = lazy(() => import('./pages/admin/FleetMap'));
const DriversPage = lazy(() => import('./pages/admin/DriversPage'));
const SafetyDashboard = lazy(() => import('./pages/SafetyDashboard'));
const SafetyDrivers = lazy(() => import('./pages/SafetyDrivers'));
const SafetyRisk = lazy(() => import('./pages/SafetyRisk'));
const SafetyAnalytics = lazy(() => import('./pages/SafetyAnalytics'));
const SpeedingAnalytics = lazy(() => import('./pages/SpeedingAnalytics'));
const SpeedMonitor = lazy(() => import('./pages/SpeedMonitor'));
const SafetySettings = lazy(() => import('./pages/SafetySettings'));
const SafetyAlerts = lazy(() => import('./pages/Safety/Alerts'));
const TrendsDashboard = lazy(() => import('./pages/Analytics/TrendsDashboard'));
const RoutingPage = lazy(() => import('./pages/Routing'));
const RouteLibrary = lazy(() => import('./pages/Routing/RouteLibrary'));
const ClientDirectory = lazy(() => import('./pages/Routing/ClientDirectory'));
const RouteAnalytics = lazy(() => import('./pages/Routing/RouteAnalytics'));

// Lazy loading for auth pages (not on critical path)
const LoginPage = lazy(() => import('./components/Auth/LoginPage'));
const UnauthorizedPage = lazy(() => import('./components/Auth/UnauthorizedPage'));

// Lazy loading for layout
const AppLayout = lazy(() => import('./components/Layout/AppLayout'));

/**
 * Page-level error fallback rendered by per-route ErrorBoundary instances.
 * Keeps the app shell (navigation, header) intact so users can navigate away
 * without a full-page reload.
 *
 * @param {{ error: Error|null, onRetry: () => void }} props
 */
function PageErrorFallback({ error, onRetry }) {
  return (
    <div className="page-error-fallback" role="alert">
      <div className="page-error-fallback__content">
        <h2 className="page-error-fallback__title">This page encountered an error</h2>
        <p className="page-error-fallback__message">
          The rest of the application is still available. You can try again or navigate to another page.
        </p>
        <div className="page-error-fallback__actions">
          <button className="retry-button" onClick={onRetry}>
            Try Again
          </button>
          <button className="reload-button" onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
        {import.meta.env.MODE === 'development' && error && (
          <details className="error-details" style={{ whiteSpace: 'pre-wrap' }}>
            <summary>Error Details (Development Only)</summary>
            <div>{error.toString()}</div>
          </details>
        )}
      </div>
    </div>
  );
}

function App() {
  const { instance, inProgress } = useMsal();
  const location = useLocation();

  // Get page title based on current route
  const getPageTitle = useCallback(() => {
    switch (location.pathname) {
      case '/dashboard':
        return 'Operations Dashboard';
      case '/buses':
        return 'Bus Fleet Management';
      case '/schedule':
        return 'Schedule Management';
      case '/scheduler':
        return 'Fleet Scheduler';
      case '/maintenance':
        return 'Maintenance';
      case '/tv-display':
        return 'TV Dashboard';
      case '/analytics':
        return 'Analytics & Reports';
      case '/analytics/trends':
        return 'Historical Trends';
      case '/audit-logs':
        return 'Audit Logs';
      case '/conflicts/history':
        return 'Conflict History';
      case '/sms/templates':
        return 'SMS Templates';
      case '/sms/broadcast':
        return 'SMS Broadcast';
      case '/sms/logs':
        return 'SMS Delivery Logs';
      case '/admin/event-triggers':
        return 'Event Triggers';
      case '/admin/event-triggers/new':
        return 'Create Event Trigger';
      case '/admin/fleet-map':
        return 'Live Fleet Map';
      case '/safety':
        return 'Safety Dashboard';
      case '/safety/drivers':
        return 'Driver Safety Scores';
      case '/safety/risk':
        return 'Risk Dashboard';
      case '/safety/analytics':
        return 'Safety Analytics';
      case '/safety/speeding':
        return 'Speeding Analytics';
      case '/safety/speed-monitor':
        return 'Speed Monitor';
      case '/safety/alerts':
        return 'Safety Alerts';
      case '/safety/settings':
        return 'Safety Settings';
      case '/safety/drivers-management':
        return 'Driver Management';
      case '/routing':
        return 'Tour Planner';
      case '/routing/library':
        return 'Route Library';
      case '/routing/clients':
        return 'Client Directory';
      case '/routing/analytics':
        return 'Route Analytics';
      default:
        if (location.pathname.startsWith('/admin/event-triggers/') && location.pathname.endsWith('/edit')) {
          return 'Edit Event Trigger';
        }
        return 'Fleet Management System';
    }
  }, [location.pathname]);

  useEffect(() => {
    // Run authentication diagnostics (only in development)
    if (import.meta.env.MODE === 'development') {
      Logger.info('[App] Running authentication diagnostics...');
      const diagnostics = runAuthDiagnostics(instance);
      printDiagnostics(diagnostics);

      if (!diagnostics.environment.isValid) {
        Logger.error('[App] Critical: Authentication configuration errors detected. App may not function correctly.');
      }
    }

    // Setup API client with enhanced authentication
    setupApiClient();

    // Initialize real-time WebSocket connection only once MSAL has finished
    // its startup/redirect handling and the user is confirmed authenticated.
    // Without the inProgress guard, getAllAccounts() returns [] during MSAL
    // startup, causing WebSocket to be skipped permanently for cached sessions.
    const accounts = instance.getAllAccounts();
    if (import.meta.env.VITE_AUTH_MODE !== 'demo' && inProgress === InteractionStatus.None && accounts.length > 0) {
      if (!realtimeClient.isInitialized) {
        initializeRealtimeConnection()
          .then(() => {
            Logger.info('[App] Real-time WebSocket connection initialized');
          })
          .catch(error => {
            Logger.error('[App] Failed to initialize WebSocket connection:', error);
          });
      }
    } else if (inProgress === InteractionStatus.None && accounts.length === 0) {
      Logger.debug('[App] Skipping WebSocket initialization - user not authenticated');
    } else if (import.meta.env.VITE_AUTH_MODE === 'demo') {
      Logger.info('[App] Real-time WebSocket disabled in demo mode');
    }

    // Initialize Application Insights
    appInsightsService.initialize();

    // Initialize Web Vitals tracking for Real User Monitoring (RUM)
    // Issue #1004 - Phase 3: Performance Monitoring & Automation
    initializeWebVitalsTracking();

    // Track page load performance after initial load
    trackPageLoadPerformance();

    // Install debug helpers in development
    if (import.meta.env.MODE === 'development') {
      installDebugHelpers(instance);
      Logger.info('[App] Debug helpers installed. Use __authDebug in console for token inspection.');
    }

    // Log build information (production: info level only)
    // Version is injected at build time from VERSION file via vite.config.js
    const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0-dev';
    const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'development';

    if (import.meta.env.MODE === 'production') {
      Logger.info(`[App] Fleet Management System v${appVersion}`);
    } else {
      const buildInfo = {
        version: appVersion,
        buildTime: buildTime,
        mode: import.meta.env.MODE,
        timestamp: new Date().toISOString()
      };
      Logger.info('[App] Build Information:', buildInfo);
      Logger.info('[App] Enhanced Authentication: ENABLED (v2 - 2025-11-16)');
    }

    // Handle redirect response if returning from auth
    instance
      .handleRedirectPromise()
      .then(response => {
        if (response) {
          Logger.info('[App] Successfully authenticated via redirect');
          // Run diagnostics after auth (only in development)
          if (import.meta.env.MODE === 'development') {
            const postAuthDiag = runAuthDiagnostics(instance);
            if (!postAuthDiag.token?.hasRoles && !postAuthDiag.token?.hasGroups) {
              Logger.warn('[App] User authenticated but has no roles/groups. API calls may fail with 403.');
            }
          }

          // Initialize WebSocket now if it was skipped at mount because accounts weren't
          // populated yet at the moment the synchronous check ran (redirect login race).
          if (!realtimeClient.isInitialized && import.meta.env.VITE_AUTH_MODE !== 'demo') {
            Logger.info('[App] Initializing WebSocket connection after redirect login');
            appInsightsService.trackEvent('WebSocket:DeferredInitAfterRedirect');
            initializeRealtimeConnection().catch(error => {
              Logger.error('[App] Failed to initialize WebSocket after redirect:', error);
              appInsightsService.trackEvent('WebSocket:DeferredInitFailed', { error: error.message });
            });
          }
        }
      })
      .catch(error => {
        Logger.error('[App] Error handling redirect:', error);
      });
  }, [instance, inProgress]);

  // Track page views on route changes
  useEffect(() => {
    appInsightsService.trackPageView(getPageTitle(), location.pathname);
  }, [location.pathname, getPageTitle]);

  return (
    <AuthErrorBoundary>
      <AuthProvider>
        <TVDisplayRedirect>
          <CSRFProvider>
            <NotificationProvider>
              <ActivityProvider>
                <div className="App">
                  <ConnectionStatus />
                  <NotificationToastContainer />
                  {/* Top-level boundary: last resort catch-all */}
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/unauthorized" element={<UnauthorizedPage />} />

                        {/* Default route - redirect to dashboard */}
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <Dashboard />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Dashboard */}
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <Dashboard />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Home - alias for dashboard */}
                        <Route
                          path="/home"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <Dashboard />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Buses */}
                        <Route
                          path="/buses"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <Buses />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Schedule */}
                        <Route
                          path="/schedule"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <Schedule />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Scheduler Dashboard */}
                        <Route
                          path="/scheduler"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SchedulerDashboard />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Maintenance */}
                        <Route
                          path="/maintenance"
                          element={
                            <ProtectedRoute>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <Maintenance />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* TV Display - Full screen, no layout */}
                        <Route
                          path="/tv-display"
                          element={
                            <ProtectedRoute>
                              <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                <TvDisplay />
                              </ErrorBoundary>
                            </ProtectedRoute>
                          }
                        />

                        {/* Audit Logs - Admin only */}
                        <Route
                          path="/audit-logs"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <AuditLogs />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Admin Panel - Admin only */}
                        <Route
                          path="/admin"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <AdminPanel />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Conflict History - Admin and Scheduler */}
                        <Route
                          path="/conflicts/history"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <ConflictHistory />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Analytics - Admin only */}
                        <Route
                          path="/analytics"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <Analytics />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Historical Trends Dashboard - Admin only */}
                        <Route
                          path="/analytics/trends"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <TrendsDashboard />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Tour Routing - Admin only until leasing Azure AD group mapping
                            is added to roleMapping.js */}
                        <Route
                          path="/routing"
                          element={
                            <ProtectedRoute requiredRoles={['admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <RoutingPage />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/routing/library"
                          element={
                            <ProtectedRoute requiredRoles={['admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <RouteLibrary />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/routing/clients"
                          element={
                            <ProtectedRoute requiredRoles={['admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <ClientDirectory />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/routing/analytics"
                          element={
                            <ProtectedRoute requiredRoles={['admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <RouteAnalytics />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* SMS Templates - Safety and Admin roles */}
                        <Route
                          path="/sms/templates"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SMSTemplates />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* SMS Broadcast - Safety and Admin roles */}
                        <Route
                          path="/sms/broadcast"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SMSBroadcast />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* SMS Logs - Safety and Admin roles */}
                        <Route
                          path="/sms/logs"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SMSLogs />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Event Triggers - Admin only */}
                        <Route
                          path="/admin/event-triggers"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <EventTriggers />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Create Event Trigger - Admin only */}
                        <Route
                          path="/admin/event-triggers/new"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <TriggerForm mode="create" />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Edit Event Trigger - Admin only */}
                        <Route
                          path="/admin/event-triggers/:id/edit"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <TriggerForm mode="edit" />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Fleet Map - Admin only */}
                        <Route
                          path="/admin/fleet-map"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <FleetMap />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Driver Management - Safety and Admin roles (under Safety section) */}
                        <Route
                          path="/safety/drivers-management"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <DriversPage />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Safety Dashboard - Safety and Admin roles */}
                        <Route
                          path="/safety"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SafetyDashboard />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Driver Safety Scores - Safety and Admin roles */}
                        <Route
                          path="/safety/drivers"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SafetyDrivers />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Risk Dashboard - Safety and Admin roles */}
                        <Route
                          path="/safety/risk"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SafetyRisk />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Safety Analytics - Safety and Admin roles */}
                        <Route
                          path="/safety/analytics"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SafetyAnalytics />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Speeding Analytics - Safety and Admin roles */}
                        <Route
                          path="/safety/speeding"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SpeedingAnalytics />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Speed Monitor - Safety and Admin roles */}
                        <Route
                          path="/safety/speed-monitor"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SpeedMonitor />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Safety Alerts - Safety and Admin roles */}
                        <Route
                          path="/safety/alerts"
                          element={
                            <ProtectedRoute requiredRoles={['safety', 'admin']}>
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SafetyAlerts />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Safety Settings - Admin only */}
                        <Route
                          path="/safety/settings"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AppLayout>
                                <ErrorBoundary FallbackComponent={PageErrorFallback}>
                                  <SafetySettings />
                                </ErrorBoundary>
                              </AppLayout>
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </ActivityProvider>
            </NotificationProvider>
          </CSRFProvider>
        </TVDisplayRedirect>
      </AuthProvider>
    </AuthErrorBoundary>
  );
}

export default App;
